import { Organization, People } from "@/pages/Private/pages/Apollo/schema/get-people";
import { ListPerson } from "@/pages/Private/pages/List/schema/list";
import { IPersonSummary } from "@/pages/Private/redux/search/search.schema";
import { toListPerson } from "@/pages/Private/pages/Contacts/utils/toListPerson";

import { DisplayCompany, getPrimaryExperience, resolveDisplayCompany } from "./utils";

// External people carry no real timestamps; the lead card only uses these for
// campaign/list affordances that aren't rendered for company-enrich results.
const EPOCH = new Date(0);

const buildOrganization = (company: DisplayCompany): Organization => ({
	id: 0,
	name: company.name ?? "",
	startup: false,
	foundedYear: null,
	logoUrl: company.logo_url ?? null,
	websiteUrl: company.domain ?? null,
	primaryDomain: company.domain ?? null,
	industry: company.industry ?? null,
	shortDescription: company.description ?? null,
	// Industry has no dedicated lead-card slot, so surface it in the OrgCategory
	// cell (and OrgName tooltip) via the company-category result the card reads.
	scoreCompanyCategoryResult: company.industry
		? { id: 0, result: company.industry, reason: "", scoreCompanyCategory: null }
		: null,
});

// The displayed title is the real job title from the primary experience; the
// top-level `position` is the LinkedIn headline and only serves as a fallback
// (PREC-3669). The headline itself is surfaced on hover by the person card.
const resolveJobTitle = (person: IPersonSummary): string | null =>
	getPrimaryExperience(person)?.position ?? person.position ?? null;

const buildProspect = (person: IPersonSummary, company: DisplayCompany | null): People => {
	const jobTitle = resolveJobTitle(person);

	return {
		id: person.id,
		email: null,
		name: person.name,
		linkedinUrl: null,
		seniority: person.seniority ?? null,
		organization: company ? buildOrganization(company) : null,
		about: null,
		jobTitleToSeniority: jobTitle ? { id: 0, seniority: person.seniority ?? "", jobTitle } : null,
		jobChangeOrPromotion: null,
		startedCurrentPosition: null,
		sentiment: null,
	};
};

/**
 * Adapts a company-enrich (LinkedIn) person into the `ListPerson` shape the lead
 * card sub-components expect, so the result renders in the same Lead layout as
 * internal contacts. The list/campaign wrapper fields are stubbed by `toListPerson`;
 * mutation/navigation-bound sub-components (LeadName, PersonOnList, …) are not used.
 */
export const companyEnrichPersonToListPerson = (person: IPersonSummary): ListPerson =>
	toListPerson({
		id: person.id,
		createdAt: EPOCH,
		updatedAt: EPOCH,
		prospect: buildProspect(person, resolveDisplayCompany(person)),
	});
